import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Lock, LogOut, Clock, GraduationCap, Users, BookOpen, BarChart3, Bell, UserCheck, Layers, Crown, LineChart, MessageSquare, Trophy, Share2 } from "lucide-react";
import SocialChannelsTab from "@/components/admin/SocialChannelsTab";
import RegistrationControls from "@/components/admin/RegistrationControls";
import CoursesTab from "@/components/admin/CoursesTab";
import StudentsTab from "@/components/admin/StudentsTab";
import CertificatesTab from "@/components/admin/CertificatesTab";
import IndicatorTab from "@/components/admin/IndicatorTab";
import UsersTab from "@/components/admin/UsersTab";
import GroupsAndFeedbackTab from "@/components/admin/GroupsAndFeedbackTab";
import PremiumTab from "@/components/admin/PremiumTab";
import KpiDashboardTab from "@/components/admin/KpiDashboardTab";
import AdminQATab from "@/components/admin/AdminQATab";
import ReferralRewardsTab from "@/components/admin/ReferralRewardsTab";
import ReferralKpiPanel from "@/components/admin/ReferralKpiPanel";
import AdminP2PTab from "@/components/admin/AdminP2PTab";

type CertificateRequest = {
  id: string; first_name: string; last_name: string; email: string;
  phone: string; group_number: string; status: string; created_at: string;
  is_duplicate?: boolean;
};

type AdminNotification = { id: string; type: string; is_read: boolean; message: string; reference_id: string | null; created_at: string; user_id: string; };
type LocalStudent = { id: string; full_name: string | null; email: string; created_at: string; phone: string; user_id: string; progress_percent: number; completed_lessons: number; total_lessons: number; enrollments: { id: string; course_id: string; course_title: string; status: string }[]; progress: { lesson_id: string; lesson_title: string; course_id: string; exam_passed: boolean; best_score: number; video_completed: boolean }[]; };
type LocalQuestion = { id: string; question_text: string; options: string[]; correct_option_index: number; sort_order: number; question_text_am?: string; options_am?: string[]; };
type LocalLesson = { id: string; title: string; sort_order: number; description: string | null; video_url: string | null; passing_score: number; questions: LocalQuestion[]; title_am?: string; description_am?: string; };
type LocalCourse = { id: string; title: string; description: string | null; thumbnail_url: string | null; is_paid: boolean; sort_order: number; certificate_template_path: string | null; is_published: boolean; lessons: LocalLesson[]; };

const ADMIN_TOKEN_KEY = "admin_session_token";
const ADMIN_ACTOR_KEY = "admin_actor_email";

// Token format: "<expiryMs>:<sig>" — drop it if expired so we don't ship a dead token to edge fns
const isTokenValid = (t: string | null): boolean => {
  if (!t) return false;
  const expiry = parseInt(t.split(":")[0], 10);
  return !Number.isNaN(expiry) && Date.now() < expiry;
};

const Admin = () => {
  const storedToken = typeof window !== "undefined" ? sessionStorage.getItem(ADMIN_TOKEN_KEY) : null;
  const initialToken = isTokenValid(storedToken) ? storedToken : null;
  if (storedToken && !initialToken) {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch {}
  }
  const [authenticated, setAuthenticated] = useState(!!initialToken);
  const [requests, setRequests] = useState<CertificateRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const sessionTokenRef = useRef<string | null>(initialToken);
  const actorNameRef = useRef<string>(
    (typeof window !== "undefined" && initialToken && sessionStorage.getItem(ADMIN_ACTOR_KEY)) || "Admin",
  );
  const { toast } = useToast();

  const clearSession = () => {
    sessionTokenRef.current = null;
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); sessionStorage.removeItem(ADMIN_ACTOR_KEY); } catch {}
    setAuthenticated(false);
  };

  const [students, setStudents] = useState<LocalStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [courses, setCourses] = useState<LocalCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  const [adminNotifications, setAdminNotifications] = useState<AdminNotification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "registration";
    return sessionStorage.getItem("admin_active_tab") || "registration";
  });
  // Persist tab across reloads
  useEffect(() => {
    try { sessionStorage.setItem("admin_active_tab", activeTab); } catch {}
  }, [activeTab]);
  const [highlightRequestId, setHighlightRequestId] = useState<string | null>(null);
  const [highlightQAId, setHighlightQAId] = useState<string | null>(null);
  const [defaultCertTab, setDefaultCertTab] = useState<string | undefined>(undefined);
  const [reportFilterEmail, setReportFilterEmail] = useState<string | null>(null);
  const [studentSearchTemp, setStudentSearchTemp] = useState<string | null>(null);

  const handleLogin = async () => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: {
          action: "login",
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
        },
      });
      if (res.error || !res.data?.success) {
        // Edge functions return the real reason (bad credentials, lockout,
        // misconfiguration) in the non-2xx body — surface it instead of a generic message.
        let message = res.data?.error as string | undefined;
        if (!message && res.error) {
          try {
            const raw = await (res.error as any)?.context?.text?.();
            message = raw ? (JSON.parse(raw)?.error ?? raw) : undefined;
          } catch { /* keep fallback */ }
        }
        toast({ title: message || "Invalid credentials", variant: "destructive" });
        return;
      }
      sessionTokenRef.current = res.data.sessionToken;
      actorNameRef.current = loginEmail.trim() || "Admin";
      try {
        sessionStorage.setItem(ADMIN_TOKEN_KEY, res.data.sessionToken);
        sessionStorage.setItem(ADMIN_ACTOR_KEY, actorNameRef.current);
      } catch {}
      setLoginEmail(""); setLoginPassword("");
      setAuthenticated(true);
      fetchRequests();
    } catch (e) {
      toast({ title: (e as Error)?.message || "Login failed", variant: "destructive" });
    }
  };


  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list", sessionToken: sessionTokenRef.current },
      });
      if (res.data?.error === "Session expired. Please log in again.") {
        clearSession();
        toast({ title: "Session expired", variant: "destructive" }); return;
      }
      if (res.data?.requests) setRequests(res.data.requests);
    } catch {
      toast({ title: "Failed to load requests", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const handleAction = async (id: string, action: "approve" | "reject" | "delete") => {
    setProcessingId(id);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action, sessionToken: sessionTokenRef.current, requestId: id },
      });
      if (res.data?.error === "Session expired. Please log in again.") {
        clearSession();
        toast({ title: "Session expired", variant: "destructive" }); return;
      }
      if (res.error || !res.data?.success) throw new Error(res.data?.error || "Action failed");
      const messages: Record<string, { title: string; description: string }> = {
        approve: { title: "Certificate sent!", description: "Certificate emailed to the user." },
        reject: { title: "Request rejected", description: "Request has been rejected." },
        delete: { title: "Request deleted", description: "Request has been removed." },
      };
      toast(messages[action]);
      fetchRequests();
    } catch (err) {
      const error = err as Error;
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setProcessingId(null); }
  };

  const fetchStudents = async () => {
    setStudentsLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_students", sessionToken: sessionTokenRef.current },
      });
      if (res.data?.students) setStudents(res.data.students);
    } catch {
      toast({ title: "Failed to load students", variant: "destructive" });
    } finally { setStudentsLoading(false); }
  };

  const fetchCourses = async () => {
    setCoursesLoading(true);
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "list_courses", sessionToken: sessionTokenRef.current },
      });
      if (res.data?.courses) setCourses(res.data.courses);
    } catch {
      toast({ title: "Failed to load courses", variant: "destructive" });
    } finally { setCoursesLoading(false); }
  };

  const fetchAdminNotifications = async () => {
    try {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "admin_notifications", sessionToken: sessionTokenRef.current },
      });
      if (res.data?.notifications) setAdminNotifications(res.data.notifications);
    } catch (err) { console.error("Admin notifications check failed:", err); }
  };

  const handleOpenAdminNotifications = async (isOpen: boolean) => {
    setNotifOpen(isOpen);
    if (isOpen) {
      const res = await supabase.functions.invoke("manage-certificates", {
        body: { action: "admin_notifications", sessionToken: sessionTokenRef.current },
      });
      const freshNotifs = res.data?.notifications || [];
      setAdminNotifications(freshNotifs);
    }
  };

  const handleMarkAllAdminRead = async () => {
    const unread = adminNotifications.filter((n) => !n.is_read);
    if (unread.length === 0) return;
    await supabase.functions.invoke("manage-certificates", {
      body: { action: "mark_admin_notifications_read", sessionToken: sessionTokenRef.current },
    });
    setAdminNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleNotificationClick = async (notification: AdminNotification) => {
    setNotifOpen(false);
    setAdminNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n))
    );
    // Mark just this one as read on the server
    await supabase.functions.invoke("manage-certificates", {
      body: { action: "mark_admin_notifications_read", sessionToken: sessionTokenRef.current, notificationIds: [notification.id] },
    });

    const t = notification.type;
    const msg = notification.message || "";

    if (t === "admin_enrollment_request") {
      const emailMatch = msg.match(/\(([^)]+@[^)]+)\)/) || msg.match(/([^\s<>]+@[^\s<>]+)/);
      const studentEmail = emailMatch?.[1] || notification.reference_id || null;
      setStudentSearchTemp(studentEmail);
      setReportFilterEmail(null);
      setHighlightRequestId(null);
      setDefaultCertTab(undefined);
      setActiveTab("students");
      fetchStudents(); fetchCourses();
      toast({ title: "Opening Students tab", description: studentEmail ? `Filtering by ${studentEmail}` : "Showing pending requests" });
    } else if (t === "download_attempt" || t === "policy_warning") {
      const emailMatch = msg.match(/\(([^)]+@[^)]+)\)/) || msg.match(/([^\s<>]+@[^\s<>]+)/);
      const studentEmail = emailMatch?.[1] || null;
      setReportFilterEmail(studentEmail);
      setStudentSearchTemp(studentEmail);
      setHighlightRequestId(null);
      setDefaultCertTab(undefined);
      setActiveTab("students");
      fetchStudents(); fetchCourses();
      toast({ title: "Opening Students tab", description: studentEmail ? `Showing reports for ${studentEmail}` : "Reports panel" });
    } else if (t === "admin_cert_request") {
      setHighlightRequestId(notification.reference_id || null);
      // Resolve cert tab once requests load
      const findAndSetTab = (reqs: CertificateRequest[]) => {
        const matched = reqs.find(r => r.id === notification.reference_id);
        if (matched && matched.group_number?.startsWith("course-")) {
          setDefaultCertTab(matched.group_number.replace("course-", ""));
        } else {
          setDefaultCertTab("legacy");
        }
      };
      findAndSetTab(requests);
      // Also re-fetch and re-resolve in case requests is stale
      try {
        const res = await supabase.functions.invoke("manage-certificates", {
          body: { action: "list", sessionToken: sessionTokenRef.current },
        });
        if (res.data?.requests) {
          setRequests(res.data.requests);
          findAndSetTab(res.data.requests);
        }
      } catch {/* ignore */}
      setActiveTab("certificates");
      fetchCourses();
      toast({ title: "Opening Certificates tab" });
    } else if (t === "lesson_qa" || t === "admin_lesson_qa") {
      setHighlightQAId(notification.reference_id || null);
      setActiveTab("qa");
      toast({ title: "Opening Q&A tab" });
    } else if (t === "admin_referral_reward") {
      setActiveTab("referrals");
      toast({ title: "Opening Referrals tab" });
    } else if (t === "admin_feedback") {
      setActiveTab("groups-feedback");
      toast({ title: "Opening Groups & Feedback tab" });
    } else if (t === "admin_action") {
      // Generic admin action — no specific destination
      toast({ title: "Notification opened", description: msg.slice(0, 100) });
    } else {
      toast({ title: "Notification opened", description: `Type: ${t}` });
    }
  };

  // On mount with restored session, load initial certificate requests once
  useEffect(() => {
    if (authenticated && sessionTokenRef.current) {
      fetchRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live admin notifications via realtime — pushes new admin alerts immediately
  useEffect(() => {
    if (!authenticated) return;
    fetchAdminNotifications();
    const adminTypes = [
      "admin_cert_request",
      "admin_enrollment_request",
      "download_attempt",
      "policy_warning",
      "admin_referral_reward",
      "admin_feedback",
      "admin_lesson_qa",
      "lesson_qa",
      "admin_action",
    ];
    const channel = supabase
      .channel("admin-notifications-live")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `type=in.(${adminTypes.join(",")})`,
        },
        () => { fetchAdminNotifications(); },
      )
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("Admin notifications realtime subscription failed:", status, err);
        }
      });
    return () => { supabase.removeChannel(channel); };
  }, [authenticated]);

  const unreadAdminNotifs = adminNotifications.filter(n => !n.is_read).length;
  const [notifFilter, setNotifFilter] = useState<"unread" | "read" | "all">("unread");
  const filteredNotifs = adminNotifications.filter((n) => {
    if (notifFilter === "unread") return !n.is_read;
    if (notifFilter === "read") return n.is_read;
    return true;
  });

  const notifTypeLabel = (t: string) => {
    switch (t) {
      case "admin_cert_request": return "Certificate";
      case "admin_enrollment_request": return "Enrollment";
      case "download_attempt": return "Violation";
      case "policy_warning": return "Policy";
      case "admin_referral_reward": return "Referral";
      case "admin_feedback": return "Feedback";
      case "admin_lesson_qa":
      case "lesson_qa": return "Q&A";
      case "admin_action": return "Action";
      default: return t.replace(/_/g, " ");
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Lock className="w-10 h-10 mx-auto mb-2 text-primary" />
            <CardTitle>Admin Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input id="admin-email" type="email" placeholder="admin@fitawrari.com" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input id="admin-password" type="password" placeholder="Enter password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
            </div>
            <Button className="w-full" onClick={handleLogin}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => { clearSession(); toast({ title: "Logged out successfully" }); }}>
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
          <div className="flex items-center gap-3">
            <Popover open={notifOpen} onOpenChange={handleOpenAdminNotifications}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative">
                  <Bell className="w-4 h-4" />
                  {unreadAdminNotifs > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] rounded-full flex items-center justify-center font-bold">
                      {unreadAdminNotifs > 9 ? "9+" : unreadAdminNotifs}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-96 p-0" align="end">
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <h4 className="font-semibold text-sm">Admin Notifications</h4>
                  {unreadAdminNotifs > 0 && (
                    <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={handleMarkAllAdminRead}>
                      Mark all read
                    </Button>
                  )}
                </div>
                <div className="px-3 pt-2 pb-1 flex items-center gap-1 border-b border-border bg-muted/30">
                  {(["unread", "read", "all"] as const).map((f) => {
                    const count = f === "unread" ? unreadAdminNotifs : f === "read" ? adminNotifications.length - unreadAdminNotifs : adminNotifications.length;
                    return (
                      <button
                        key={f}
                        onClick={() => setNotifFilter(f)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors capitalize ${
                          notifFilter === f
                            ? "bg-primary text-primary-foreground font-semibold"
                            : "text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {f} ({count})
                      </button>
                    );
                  })}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {filteredNotifs.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground text-center">No {notifFilter === "all" ? "" : notifFilter} notifications</p>
                  ) : (
                    filteredNotifs.map((n) => (
                      <div key={n.id} onClick={() => handleNotificationClick(n)} className={`px-3 py-2.5 border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 transition-colors ${!n.is_read ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}>
                        <div className="flex items-start gap-2">
                          {!n.is_read ? (
                            <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                          ) : (
                            <span className="mt-1.5 w-2 h-2 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-[10px] uppercase tracking-wide font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                                {notifTypeLabel(n.type)}
                              </span>
                            </div>
                            <p className={`text-sm break-words ${!n.is_read ? "font-semibold" : "text-muted-foreground"}`}>{n.message}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <h1 className="text-2xl font-display font-bold">Admin Panel</h1>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          if (val === "certificates") { fetchRequests(); fetchCourses(); }
          if (val === "students") { fetchStudents(); fetchCourses(); }
          if (val === "courses") fetchCourses();
        }}>
          <ScrollArea className="w-full">
            <TabsList className="admin-tabs-list inline-flex w-auto min-w-full [&_[data-state=active]]:bg-primary [&_[data-state=active]]:text-primary-foreground">
              <TabsTrigger value="registration" className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> <span className="hidden sm:inline">Registration</span>
              </TabsTrigger>
              <TabsTrigger value="certificates" className="flex items-center gap-1.5">
                <GraduationCap className="w-4 h-4" /> <span className="hidden sm:inline">Certificates</span>
              </TabsTrigger>
              <TabsTrigger value="courses" className="flex items-center gap-1.5">
                <BookOpen className="w-4 h-4" /> <span className="hidden sm:inline">Courses</span>
              </TabsTrigger>
              <TabsTrigger value="students" className="flex items-center gap-1.5">
                <Users className="w-4 h-4" /> <span className="hidden sm:inline">Students</span>
              </TabsTrigger>
              <TabsTrigger value="premium" className="flex items-center gap-1.5">
                <Crown className="w-4 h-4" /> <span className="hidden sm:inline">Premium</span>
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-1.5">
                <UserCheck className="w-4 h-4" /> <span className="hidden sm:inline">Users</span>
              </TabsTrigger>
              <TabsTrigger value="groups-feedback" className="flex items-center gap-1.5">
                <Layers className="w-4 h-4" /> <span className="hidden sm:inline">Groups & Feedback</span>
              </TabsTrigger>
              <TabsTrigger value="qa" className="flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4" /> <span className="hidden sm:inline">Q&A</span>
              </TabsTrigger>
              <TabsTrigger value="social" className="flex items-center gap-1.5">
                <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Social</span>
              </TabsTrigger>
              <TabsTrigger value="growth" className="flex items-center gap-1.5">
                <Trophy className="w-4 h-4" /> <span className="hidden sm:inline">Referrals & P2P</span>
              </TabsTrigger>
              <TabsTrigger value="indicator" className="flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> <span className="hidden sm:inline">Indicator</span>
              </TabsTrigger>
              <TabsTrigger value="kpi" className="flex items-center gap-1.5">
                <LineChart className="w-4 h-4" /> <span className="hidden sm:inline">KPIs</span>
              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <TabsContent value="kpi">
            <KpiDashboardTab />
          </TabsContent>

          <TabsContent value="registration">
            <RegistrationControls sessionToken={sessionTokenRef.current!} />
          </TabsContent>

          <TabsContent value="certificates">
            <CertificatesTab
              sessionToken={sessionTokenRef.current!}
              requests={requests}
              loading={loading}
              fetchRequests={fetchRequests}
              handleAction={handleAction}
              processingId={processingId}
              courses={courses as unknown as typeof courses}
              fetchCourses={fetchCourses}
              highlightRequestId={highlightRequestId}
              defaultCertTab={defaultCertTab}
            />
          </TabsContent>

          <TabsContent value="premium">
          <PremiumTab sessionToken={sessionTokenRef.current} />
        </TabsContent>

        <TabsContent value="courses">
            <CoursesTab
              sessionToken={sessionTokenRef.current!}
              courses={courses as unknown as typeof courses}
              coursesLoading={coursesLoading}
              fetchCourses={fetchCourses}
            />
          </TabsContent>

          <TabsContent value="students">
            <StudentsTab
              sessionToken={sessionTokenRef.current!}
              students={students as unknown as typeof students}
              studentsLoading={studentsLoading}
              fetchStudents={fetchStudents}
              courses={courses as unknown as typeof courses}
              initialSearch={studentSearchTemp}
              onSearchClear={() => setStudentSearchTemp(null)}
              onViewReports={(email) => { setReportFilterEmail(email); setActiveTab("students"); }}
              reportFilterEmail={reportFilterEmail}
              onReportFilterClear={() => setReportFilterEmail(null)}
            />
          </TabsContent>

          <TabsContent value="indicator">
            <IndicatorTab sessionToken={sessionTokenRef.current!} />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab sessionToken={sessionTokenRef.current!} />
          </TabsContent>

          <TabsContent value="groups-feedback">
            <GroupsAndFeedbackTab sessionToken={sessionTokenRef.current!} />
          </TabsContent>

          <TabsContent value="qa">
            <AdminQATab
              sessionToken={sessionTokenRef.current!}
              highlightQuestionId={highlightQAId}
              onHighlightHandled={() => setHighlightQAId(null)}
            />
          </TabsContent>

          <TabsContent value="social">
            <SocialChannelsTab sessionToken={sessionTokenRef.current} />
          </TabsContent>

          <TabsContent value="growth">
            <Tabs defaultValue="referrals-approvals" className="space-y-4">
              <TabsList>
                <TabsTrigger value="referrals-approvals">Referral approvals</TabsTrigger>
                <TabsTrigger value="referrals-kpi">Referral KPIs</TabsTrigger>
                <TabsTrigger value="p2p">P2P</TabsTrigger>
              </TabsList>
              <TabsContent value="referrals-approvals">
                <ReferralRewardsTab sessionToken={sessionTokenRef.current!} />
              </TabsContent>
              <TabsContent value="referrals-kpi">
                <ReferralKpiPanel />
              </TabsContent>
              <TabsContent value="p2p">
                <AdminP2PTab sessionToken={sessionTokenRef.current!} actorName={actorNameRef.current} />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
